import useAuthUser from "../../hooks/useAuthUser";
import useModal from "../../hooks/useModal";
import TableLayout from "../layout/TableLayout";
import {
  NoRecordsFound,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../Table";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import CheckIcon from "@mui/icons-material/Check";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import { TextField, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import useConfirm from "../../hooks/useConfirm";
import { capitalizeFirstLetter } from "../../Helper/functionHelper";
import { approveRejectCancel } from "../../services/odd";
import { toast } from "react-toastify";
import { useState } from "react";
import ExpandableText from "../common/ExpandableText";
import UserInfoCell from "../common/UserInfoCell";
import Button from "../ui/Button";

const ODD_STATUS_MAP = {
  Pending: { getStatus: () => "Pending", className: "text-warning" },
  Approved: {
    getStatus: (name) => `Approved by ${name}`,
    className: "text-success",
  },
  Rejected: {
    getStatus: (name) => `Rejected by ${name}`,
    className: "text-danger",
  },
  Cancelled: {
    getStatus: (name) => `Cancelled by ${name}`,
    className: "text-danger",
  },
};

const ODDList = ({ oddData = [], activeFilters, onFilter }) => {
  const tableHeaders = [
    "ODD Type",
    "Date From",
    "Date To",
    "No. of Days",
    "Reason",
    "Approve/Reject Reason",
    "ODD Status",
    "Applied On",
    "Actioned On",
    "Action",
  ];
  const { isAdminHrRole, isManagerRole, isEmployeeRole } = useAuthUser();
  const { showModal, hideModal } = useModal();
  const { confirm } = useConfirm();
  if (isAdminHrRole || isManagerRole) {
    tableHeaders.unshift("Reporting Manager");
    tableHeaders.unshift("Department");
    tableHeaders.unshift("Designation");
    tableHeaders.unshift("Employee Name (Code)");
  }

  const openModal = (title, body) => {
    showModal(title, body);
  };

  const handleODDActions = async (oddId, action, remarks = "") => {
    let cancelText = "";
    if (action === "cancel") cancelText = "No";
    const isConfirmed = await confirm({
      title: `${capitalizeFirstLetter(action)} ODD`,
      content: `Are you sure you want to ${action} this ODD?`,
      confirmText: `Yes ${capitalizeFirstLetter(action)}`,
      cancelText: cancelText,
    });

    if (isConfirmed) {
      try {
        const res = await approveRejectCancel(oddId, action, remarks);
        if (res && res.status === 200) {
          const message = res.data?.message || "ODD updated successfully.";
          toast.success(message);
          onFilter(activeFilters);
        } else {
          const errorMessage =
            res?.data?.message || "Something went wrong. Please try again.";
          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response) {
          const errorMessage = error.response.data?.message || "Server error.";
          toast.error(errorMessage);
        } else {
          toast.error("Network error. Please check your connection.");
        }
      }
    }
  };

  const handleApproveReject = (oddId, action) => {
    const title = `${capitalizeFirstLetter(action)} ODD`;

    const ApproveRejectForm = () => {
      const [reason, setReason] = useState("");
      const [error, setError] = useState("");
      const [loading, setLoading] = useState(false);

      const handleSubmit = async () => {
        if (action === "reject" && reason.trim() === "") {
          setError("Reason is required for rejection.");
          return;
        }

        setLoading(true);

        try {
          const res = await approveRejectCancel(oddId, action, reason);
          if (res?.status === 200) {
            toast.success(res.data?.message || "ODD updated successfully.");
            onFilter(activeFilters);
            hideModal();
            setReason("");
            setError("");
          } else {
            toast.error(res?.data?.message || "Something went wrong.");
          }
        } catch (error) {
          toast.error(
            error?.response?.data?.message || "An unexpected error occurred."
          );
        } finally {
          setLoading(false);
        }
      };

      return (
        <div className="py-2">
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            label="Reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError("");
            }}
            error={!!error}
            helperText={error}
          />
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="dark"
              className="me-2"
              onClick={() => hideModal()}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => {
                handleSubmit();
              }}
              disabled={loading}
            >
              {capitalizeFirstLetter(action)}
            </Button>
          </div>
        </div>
      );
    };

    openModal(title, <ApproveRejectForm />);
  };

  return (
    <>
      <TableLayout>
        <Table className="table-col-freeze">
          <TableHeader>
            <TableRow>
              {tableHeaders.map((head, index) => (
                <TableHead
                  key={index}
                  className={
                    index === tableHeaders.length - 1 ? "last-col-freeze" : ""
                  }
                >
                  {head}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {oddData.length > 0 ? (
              oddData.map((item, index) => {
                const {
                  status,
                  actionerName,
                  userToken,
                  employeeName,
                  employeeId,
                  reason,
                  designation,
                  department,
                  approveRejectReason,
                  reportingManager,
                  oddType,
                  startDate,
                  endDate,
                  oddInDays,
                  createdAt,
                  actionedOn,
                  id,
                } = item;
                const statusData = ODD_STATUS_MAP[status] || {
                  getStatus: () => "--",
                  className: "",
                };
                const statusLabel = statusData.getStatus(actionerName);
                return (
                  <TableRow key={index}>
                    {(isAdminHrRole || isManagerRole) && (
                      <>
                        <TableCell>
                          <UserInfoCell
                            name={employeeName}
                            employeeId={employeeId}
                            token={userToken}
                          />
                        </TableCell>
                        <TableCell>{designation}</TableCell>
                        <TableCell>{department}</TableCell>
                        <TableCell>{reportingManager}</TableCell>
                      </>
                    )}
                    <TableCell>{oddType}</TableCell>
                    <TableCell>{startDate}</TableCell>
                    <TableCell>{endDate}</TableCell>
                    <TableCell>{oddInDays}</TableCell>
                    <TableCell>
                      <ExpandableText text={reason} label="ODD Reason" />
                    </TableCell>
                    <TableCell>
                      <ExpandableText
                        text={approveRejectReason}
                        label="Approve/Reject Reason"
                      />
                    </TableCell>
                    <TableCell>
                      <span className={statusData.className}>
                        {statusLabel}
                      </span>
                    </TableCell>
                    <TableCell>{createdAt}</TableCell>
                    <TableCell>{actionedOn || "--"}</TableCell>
                    <TableCell className="last-col-freeze">
                      {isEmployeeRole && status === "Pending" && (
                        <>
                          <Tooltip title="Cancel" placement="top" arrow>
                            <ClearOutlinedIcon
                              className="cursor-pointer"
                              color="error"
                              onClick={() => {
                                handleODDActions(id, "cancel");
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="Edit" placement="top" arrow>
                            <Link to={`/odd/edit/${id}`} className="text-dark">
                              <CreateOutlinedIcon className="cursor-pointer" />
                            </Link>
                          </Tooltip>
                        </>
                      )}
                      {(isManagerRole || isAdminHrRole) &&
                        status === "Pending" && (
                          <>
                            <Tooltip title="Approve" placement="top" arrow>
                              <CheckIcon
                                className="cursor-pointer"
                                color="success"
                                onClick={() => {
                                  handleApproveReject(id, "approve");
                                }}
                              />
                            </Tooltip>
                            <Tooltip title="Reject" placement="top" arrow>
                              <DoNotDisturbAltIcon
                                className="cursor-pointer"
                                color="error"
                                onClick={() => {
                                  handleApproveReject(id, "reject");
                                }}
                              />
                            </Tooltip>
                          </>
                        )}
                      {(isManagerRole || isAdminHrRole) &&
                        status === "Approved" &&
                        (() => {
                          const now = new Date();
                          const end = new Date(endDate);
                          const fourDaysAgo = new Date(
                            now.getTime() - 4 * 24 * 60 * 60 * 1000
                          );
                          return end >= fourDaysAgo;
                        })() && (
                          <Tooltip title="Cancel" placement="top" arrow>
                            <ClearOutlinedIcon
                              className="cursor-pointer"
                              color="error"
                              onClick={() => {
                                handleODDActions(id, "cancel");
                              }}
                            />
                          </Tooltip>
                        )}
                      {isEmployeeRole &&
                        status === "Approved" &&
                        (() => {
                          const start = new Date(startDate);
                          const now = new Date();
                          return start > now;
                        })() && (
                          <Tooltip title="Cancel" placement="top" arrow>
                            <ClearOutlinedIcon
                              className="cursor-pointer"
                              color="error"
                              onClick={() => {
                                handleODDActions(id, "cancel");
                              }}
                            />
                          </Tooltip>
                        )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <NoRecordsFound colSpan={tableHeaders.length} />
            )}
          </TableBody>
        </Table>
      </TableLayout>
    </>
  );
};

export default ODDList;
